import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DollarSign, AlertTriangle, Clock, TrendingUp,
  Ship, Loader2, RefreshCw, Lightbulb
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell
} from 'recharts';

function StatCard({ title, value, icon: Icon, color, sub }) {
  return (
    <Card className="bg-white border border-slate-200 rounded-sm shadow-none hover:border-slate-300 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{title}</p>
            <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${color}`}>
            <Icon className="h-4 w-4 text-white stroke-[1.5px]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DemurrageForecastPage() {
  const [vessels, setVessels] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [v, f] = await Promise.all([
        api.get('/scheduling/vessels'),
        api.get('/scheduling/demurrage-forecasts'),
      ]);
      setVessels(v.data);
      setForecasts(f.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleForecast = async (vesselId) => {
    setGenerating(vesselId);
    try {
      await api.get(`/scheduling/vessels/${vesselId}/demurrage-forecast`);
      const { data } = await api.get('/scheduling/demurrage-forecasts');
      setForecasts(data);
    } catch (e) { console.error(e); }
    finally { setGenerating(null); }
  };

  const handleForecastAll = async () => {
    setGenerating('all');
    try {
      for (const v of vessels) {
        await api.get(`/scheduling/vessels/${v.id}/demurrage-forecast`);
      }
      const { data } = await api.get('/scheduling/demurrage-forecasts');
      setForecasts(data);
    } catch (e) { console.error(e); }
    finally { setGenerating(null); }
  };

  const totalExposure = forecasts.reduce((s, f) => s + parseFloat(f.projected_demurrage || 0), 0);
  const alertCount = forecasts.filter(f => f.alert_triggered).length;
  const avgExcess = forecasts.length > 0
    ? (forecasts.reduce((s, f) => s + parseFloat(f.projected_excess_hours || 0), 0) / forecasts.length).toFixed(1)
    : '0.0';

  const chartData = forecasts.slice(0, 10).map(f => ({
    name: f.vessel_name || 'Unknown',
    demurrage: parseFloat(f.projected_demurrage || 0),
    alert: f.alert_triggered,
  }));

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="demurrage-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="demurrage-forecast-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Demurrage Forecast</h1>
          <p className="text-sm text-slate-500 mt-1">Predictive demurrage exposure analysis & recommendations</p>
        </div>
        <Button
          onClick={handleForecastAll}
          disabled={generating === 'all' || vessels.length === 0}
          data-testid="forecast-all-button"
          className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white text-xs"
        >
          {generating === 'all' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Forecast All Vessels
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="demurrage-kpis">
        <StatCard title="Total Exposure" value={`$${totalExposure.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} icon={DollarSign} color="bg-[#0A2463]" sub={`${forecasts.length} forecasts`} />
        <StatCard title="Active Alerts" value={alertCount} icon={AlertTriangle} color="bg-[#E11D48]" sub="Exceeding threshold" />
        <StatCard title="Avg Excess Hours" value={`${avgExcess}h`} icon={Clock} color="bg-[#F59E0B]" sub="Above allowed laytime" />
        <StatCard title="Fleet Size" value={vessels.length} icon={Ship} color="bg-[#16A34A]" sub="Tracked vessels" />
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Demurrage Exposure by Vessel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={(v) => `$${v.toLocaleString()}`} />
                  <Bar dataKey="demurrage" radius={[2, 2, 0, 0]} barSize={28}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.alert ? '#E11D48' : '#0A2463'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vessel Forecast Cards */}
      <div className="space-y-3">
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
          Vessel Forecasts ({vessels.length})
        </p>
        {vessels.map(v => {
          const vForecasts = forecasts.filter(f => f.vessel_id === v.id);
          const latest = vForecasts[0];
          return (
            <Card key={v.id} className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid={`demurrage-vessel-${v.imo}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-sm bg-slate-100 flex items-center justify-center">
                      <Ship className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{v.name}</p>
                      <p className="text-[10px] font-mono text-slate-400">IMO {v.imo}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {latest?.alert_triggered && (
                      <Badge className="rounded-sm text-[9px] bg-red-100 text-red-700 border border-red-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />ALERT
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleForecast(v.id)}
                      disabled={generating === v.id}
                      data-testid={`forecast-button-${v.imo}`}
                      className="rounded-sm text-xs border-slate-200"
                    >
                      {generating === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <TrendingUp className="h-3 w-3 mr-1" />}
                      Forecast
                    </Button>
                  </div>
                </div>

                {latest && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 border-t border-slate-100 pt-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">ETA</p>
                      <p className="text-xs font-mono text-slate-700 mt-0.5">{latest.predicted_eta ? new Date(latest.predicted_eta).toLocaleDateString() : '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Allowed</p>
                      <p className="text-xs font-mono text-slate-700 mt-0.5">{latest.allowed_hours}h</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Projected</p>
                      <p className="text-xs font-mono text-slate-700 mt-0.5">{parseFloat(latest.projected_used_hours).toFixed(1)}h</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Excess</p>
                      <p className={`text-xs font-mono mt-0.5 ${parseFloat(latest.projected_excess_hours) > 0 ? 'text-red-600 font-bold' : 'text-slate-700'}`}>
                        {parseFloat(latest.projected_excess_hours).toFixed(1)}h
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Demurrage</p>
                      <p className={`text-xs font-mono font-bold mt-0.5 ${latest.alert_triggered ? 'text-red-600' : 'text-slate-900'}`}>
                        ${parseFloat(latest.projected_demurrage).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {latest?.recommendation && (
                  <div className="mt-3 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-sm" data-testid={`recommendation-${v.imo}`}>
                    <Lightbulb className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">{latest.recommendation}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
